/**
 * In-place navigation for the collection page.
 *
 * Filters, sorting, pagination and (sub)category links fetch the collection section
 * through the Section Rendering API and swap only the regions that change, so the header,
 * cart, fonts and already-decoded product images survive the update.
 *
 * A swap ends by re-running the same init a fresh page load runs, so the result is
 * indistinguishable from a reload.
 */
(() => {
  const root = document.querySelector('[data-collection-section]');
  const sectionId = root?.dataset.sectionId;
  if (!root || !sectionId) return;

  /**
   * Swapped per region rather than as one whole section: the mobile filter modal would
   * lose its open state, and the category scroller its listeners.
   */
  const REGIONS = [
    'collection-title',
    'category-buttons',
    'category-buttons-mobile',
    'metafield-filters-container',
    'metafield-filters-container-mobile',
    'collection-toolbar',
    'collection-results',
    'collection-description',
  ];

  /** Regions whose parent owns scroll listeners, so only their contents may be replaced. */
  const INNER = new Set(['category-buttons', 'category-buttons-mobile']);

  const FILTER_LINKS = [
    '[data-filter-type="native"] a',
    '[data-filter-type="subcategory"] a',
    '#pagination-container a',
    '#category-buttons a',
    '#category-buttons-mobile a',
  ]
    .map((selector) => `${selector}:not([aria-disabled="true"])`)
    .join(', ');

  const parser = new DOMParser();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  let controller;
  /** What is on screen, which is not location: on popstate the URL has already moved. */
  let renderedUrl;

  function normalize(input) {
    const url = new URL(input, location.origin);
    url.searchParams.delete('section_id');
    url.searchParams.delete('limit');
    return url;
  }

  const toHref = ({ pathname, searchParams }) => pathname + (searchParams.size ? `?${searchParams}` : '');

  /** The section id Shopify wrapped `selector` in, e.g. "breadcrumbs". */
  const sectionIdOf = (selector) =>
    document.querySelector(selector)?.closest('.shopify-section')?.id.replace(/^shopify-section-/, '');

  async function fetchSection(url, id, signal) {
    const request = new URL(url);
    request.searchParams.set('section_id', id);

    const response = await fetch(request, { signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Section ${id} responded ${response.status}`);

    return parser.parseFromString(await response.text(), 'text/html');
  }

  function applyRegions(doc) {
    for (const id of REGIONS) {
      const current = document.getElementById(id);
      if (!current) continue;

      const next = doc.getElementById(id);
      if (!next) current.replaceChildren();
      else if (INNER.has(id)) current.replaceChildren(...next.childNodes);
      else current.replaceWith(next);
    }
  }

  /** Section responses carry their own wrapper, so a whole section swaps by id. */
  function applySection(doc, id) {
    const current = document.getElementById(`shopify-section-${id}`);
    const next = doc.getElementById(`shopify-section-${id}`);
    if (current && next) current.replaceWith(next);
  }

  const render = (update) =>
    document.startViewTransition && !reducedMotion.matches
      ? document.startViewTransition(update).updateCallbackDone
      : update();

  /**
   * @param {string} input - destination URL, absolute or root-relative
   * @param {{ push?: boolean, scroll?: boolean }} [options]
   */
  async function navigate(input, { push = true, scroll = false } = {}) {
    const url = normalize(input);
    const target = toHref(url);
    if (target === renderedUrl) return;

    controller?.abort();
    const { signal } = (controller = new AbortController());

    // Another collection moves the breadcrumb trail and the cached product data with it
    const movedCollection = url.pathname !== new URL(renderedUrl, location.origin).pathname;
    const extraIds = movedCollection
      ? [sectionIdOf('#products-metafield-data'), sectionIdOf('.breadcrumbs-section')].filter(Boolean)
      : [];

    root.setAttribute('aria-busy', 'true');

    try {
      const [main, ...extras] = await Promise.all([
        fetchSection(url, sectionId, signal),
        ...extraIds.map((id) => fetchSection(url, id, signal)),
      ]);
      if (signal.aborted) return;

      // The URL has to be current before the swap: the filter scripts read location while
      // rebuilding their state.
      history[push ? 'pushState' : 'replaceState']({ collection: true }, '', target);

      await render(() => {
        applyRegions(main);
        extraIds.forEach((id, index) => applySection(extras[index], id));
      });

      renderedUrl = target;
      window.collectionFiltersRefresh?.({ reloadProducts: movedCollection });
      document.dispatchEvent(new CustomEvent('collection:updated'));

      if (scroll) {
        document.getElementById('collection-toolbar')?.scrollIntoView({
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
          block: 'start',
        });
      }
    } catch (error) {
      if (signal.aborted) return;
      // Never leave the visitor stuck on stale results
      console.error('[collection] in-place update failed, falling back to a page load', error);
      location.assign(target);
    } finally {
      if (!signal.aborted) root.removeAttribute('aria-busy');
    }
  }

  /**
   * Capture phase, deliberately: the filter form registers its own capture listener that
   * calls stopPropagation() for native filter links, which would otherwise swallow the
   * click before it reaches us. Being first also makes this the click's only owner, so
   * the legacy per-link handlers cannot start a competing navigation.
   */
  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target instanceof Element ? event.target.closest(FILTER_LINKS) : null;
      if (!(link instanceof HTMLAnchorElement)) return;
      if (link.origin !== location.origin || !link.pathname.startsWith('/collections/')) return;

      event.preventDefault();
      event.stopPropagation();

      navigate(link.href, { scroll: Boolean(link.closest('#pagination-container')) });
    },
    { capture: true }
  );

  addEventListener('popstate', () => navigate(location.href, { push: false }));

  window.collectionNavigate = navigate;
  renderedUrl = toHref(normalize(location.href));
})();
